/**
 * Page de saisie + calcul DPE — version tabulaire accessible (NF-A11Y-09).
 *
 * Forme alternative au wizard graphique 2D (C6 à venir). Les deux écriront
 * dans le même payload (cf. BRD § 5.3.1).
 *
 * Sections : Logement / Murs / Toit / Plancher bas / Fenêtres / Portes / Systèmes.
 * Boutons : Enregistrer · Calculer.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Layout } from "@/components/Layout";
import { AutosaveIndicator } from "@/components/dpe/AutosaveIndicator";
import { DpeResultScreen } from "@/components/dpe/DpeResultScreen";
import { ConseilsSansTravaux, ReportView } from "@/components/dpe/ReportView";
import { ScenariosPanelGate } from "@/components/dpe/ScenariosPanelGate";
import { getUiFlags } from "@/api/site";
import { TutorialButton } from "@/components/dpe/TutorialButton";
import {
  WizardPlanCanvas,
  emptyPlan,
  geometryToWallSurface,
  type PlanGeometry,
} from "@/components/dpe/WizardPlanCanvas";
import {
  WizardStepper,
  type WizardStep,
} from "@/components/dpe/WizardStepper";
import { ApiError } from "@/api/client";
import { updateDpePayload, getReportData } from "@/api/dpe";
import {
  parseValidationErrors,
  countErrors,
  type SectionErrors,
} from "@/utils/validationErrors";
import { postalToDepartment } from "@/utils/postalCode";
import { useDebouncedAutosave } from "@/hooks/useDebouncedAutosave";
import { useLogement } from "@/hooks/useLogements";
import {
  useCalculateDpe,
  useCatalogs,
  useDpe,
  type CalculateProgressState,
} from "@/hooks/useDpe";
import type { Catalogs, CatalogOption, TutorialOption } from "@/api/catalogs";

// =============================================================================
// Types du payload côté frontend (miroir du DPEPayload Pydantic)
// =============================================================================

// Note : le champ `name` est purement informatif (libellé saisi par
// l'utilisateur pour identifier l'élément). Pas d'impact sur le calcul.
type InsulationYear =
  | "before_1975" | "1975_1977" | "1978_1982" | "1983_1988"
  | "1989_2000" | "2001_2005" | "from_2006" | "from_2013";

type IsolationTypeWall =
  | "ITI" | "ITE" | "ITR"
  | "ITI+ITE" | "ITI+ITR" | "ITE+ITR" | "ITI+ITE+ITR";

type Wall = {
  name?: string | null;
  material: string;
  surface_m2: number;
  insulation?: string | null;
  adjacent?: string;
  insulation_year?: InsulationYear | null;
  isolation_type?: IsolationTypeWall | null;
  insulation_thickness_cm?: number | null;
  insulation_lambda_w_m_k?: number;
  insulation_enduit_isolant?: boolean;
  aiu_aue_ratio?: number | null;
  uvue?: number | null;
  lnc_state?: "isole" | "non_isole" | null;
  paroi_state?: "isole" | "non_isole" | null;
};
type Roof = {
  name?: string | null;
  material: string;
  surface_m2: number;
  insulation?: string | null;
  adjacent?: string;
  insulation_year?: InsulationYear | null;
  isolation_type?: "ITI" | "ITE" | "ITR" | "ITI+ITE" | null;
  insulation_thickness_cm?: number | null;
  insulation_lambda_w_m_k?: number;
  aiu_aue_ratio?: number | null;
  uvue?: number | null;
  lnc_state?: "isole" | "non_isole" | null;
  paroi_state?: "isole" | "non_isole" | null;
};
type LowFloor = {
  name?: string | null;
  material: string;
  surface_m2: number;
  insulation?: string | null;
  adjacent?: string;
  insulation_year?: InsulationYear | null;
  isolation_type?: "ITI" | "ITE" | "ITR" | "Inconnu" | null;
  insulation_thickness_cm?: number | null;
  insulation_lambda_w_m_k?: number;
  perimeter_m?: number | null;
  building_surface_m2?: number | null;
  building_perimeter_m?: number | null;
  aiu_aue_ratio?: number | null;
  uvue?: number | null;
  lnc_state?: "isole" | "non_isole" | null;
  paroi_state?: "isole" | "non_isole" | null;
};
type Orientation = "N" | "NE" | "E" | "SE" | "S" | "SO" | "O" | "NO" | "horizontal";
type Window = {
  name?: string | null;
  glazing: string;
  surface_m2: number;
  // Orientation obligatoire pour le calcul des apports solaires (§ 4.2.1
  // PDF 3CL). Défaut "S" (cas le plus fréquent + maximise les apports).
  orientation?: Orientation;
  // V5 — Fenêtre de toit (Velux) : flag UI. À la saisie, on auto-set
  // baie_inclinaison="moins_25" et orientation="horizontal". Le moteur
  // 3CL utilise uniquement baie_inclinaison+orientation pour le calcul.
  is_velux?: boolean;
  baie_inclinaison?: "verticale" | "25_75" | "moins_25";
  menuiserie_type?: "bois" | "pvc" | "metal_rpt" | "metal_sans_rpt";
  type_fenetre?:
    | "battante"
    | "coulissante"
    | "porte_fenetre"
    | "porte_fenetre_soubassement";
  position_menuiserie?: "nu_exterieur" | "tunnel" | "nu_interieur";
  largeur_dormant_cm?: 5 | 10 | null;
  has_protection_solaire?: boolean;
  volet_type?:
    | "aucun"
    | "volet_bois_metal_simple"
    | "volet_persienne"
    | "volet_roulant_pvc"
    | "volet_roulant_alu_isole"
    | "jalousie_orientable";
  masque_proche?: "aucun" | "loggia" | "balcon" | "paroi_laterale_sud";
  masque_avancee_m?: number;
  baie_hauteur_m?: number | null;
  masque_lointain_angle_deg?: number | null;
};
type Door = {
  name?: string | null;
  door_type: string;
  surface_m2: number;
};

// 3CL § 7 — configurations multi-générateurs (base + appoint, etc.)
type HeatingConfig =
  | "simple"
  | "avec_solaire"
  | "avec_appoint_bois"
  | "poele_avec_sdb_electrique"
  | "systeme_plus_poele_plus_sdb"
  | "chaudiere_releve_bois"
  | "solaire_plus_appoint_bois"
  | "chaudiere_releve_pac"
  | "pac_chaudiere_avec_appoint_bois"
  | "collectif_base_appoint"
  | "multi_installations";

type HeatingSystem = {
  name?: string | null;
  generator: string;
  share: number;
  // 3CL § 8 — chaîne émission / distribution / régulation (saisissables, avec
  // override de l'inférence par défaut côté backend).
  emitter?: string;
  regulation?: string;
  distribution?: string;
  distribution_isolated?: boolean;
  // 3CL § 7 — configuration multi-générateurs (base/appoint, chaudière+poêle…)
  config?: HeatingConfig;
  nominal_power_kw?: number | null;
  // V1.4 — § 9.3/§10 : année d'installation du générateur (prime sur l'année
  // de construction pour les lookups Rg par ancienneté).
  year_install?: number | null;
  // V0.9.5/6/7 — chauffage collectif (3CL § 6 INT)
  is_collective?: boolean;
  has_individual_metering?: boolean;
  // V0.9.5/6/7 — régulation intermittence (3CL § INT)
  intermittence_equipement?:
    | "absent"
    | "central_sans_min"
    | "central_avec_min"
    | "piece_avec_min"
    | null;
};

type EcsSystem = {
  name?: string | null;
  generator: string;
  storage_volume_L?: number | null;
  distribution_isolated?: boolean;
  location?: "volume_habitable" | "hors_volume_habitable";
  // V0.9.5/6/7 — CET (3CL § 15.2)
  alimentation_cet?: "permanente" | "heure_creuse" | null;
  // V0.9.5/6/7 — Rd ECS (3CL § 13, matrice 3x3)
  pieces_contigues?: boolean;
  // V0.9.5/6/7 — bonus Rs × 1,08 ballon CET recommandé (3CL § 14.3)
  is_recommended_ballon?: boolean;
  // V0.9.7 — ECS collective d'immeuble (3CL § 13.2, matrice Rd réseau bouclage)
  is_collective_ecs?: boolean;
  collective_network_insulated?: boolean;
  // V1.4 — année d'installation du générateur ECS (prime sur l'année de
  // construction pour les lookups Rg par ancienneté).
  year_install?: number | null;
};

type Ventilation = {
  name?: string | null;
  type: string;
  has_joints?: boolean;
  has_trappe_cheminee?: boolean;
};

type Cooling = {
  percent_climatized?: number;
  energy?: "electricite" | "gaz_naturel";
  is_top_floor?: boolean;
  // V0.9.14 — Confort d'été (arbre décisionnel Guide DPE 2024 p. 104-105)
  protection_solaire?: "aucune" | "partielle" | "totale" | null;
  ventilation_traversante?: boolean;
  brasseur_air?: boolean;
};

type Renewable = {
  pv_capteurs_m2?: number;
  has_micro_eolien?: boolean;
  has_cogeneration?: boolean;
  has_chauffage_solaire?: boolean;
  has_ecs_solaire?: boolean;
  installation_solaire_ancienne?: boolean;
};

type Payload = {
  version_schema?: number;
  meta: {
    type: "maison" | "appartement";
    construction_year: number;
    shab_m2: number;
    ceiling_height_m?: number;
    department_code?: string;
    altitude_band?: "<=400" | "401-800" | ">800";
    inertia?: "legere" | "moyenne" | "lourde" | "tres_lourde";
    parois_anciennes?: boolean | null;
    // D17 — layout bâtiment : logements de l'immeuble pour la répartition
    // § 17.2.2 (méthode 1 prorata surface / méthode 2 clé de répartition).
    appartements?: { nom: string; shab_m2: number; cle_kwh?: number | null }[];
  };
  walls: Wall[];
  roof?: Roof;
  low_floor?: LowFloor;
  windows: Window[];
  doors: Door[];
  heating: HeatingSystem[];
  ecs: EcsSystem;
  ventilation: Ventilation;
  cooling?: Cooling;
  renewable?: Renewable;
};

function emptyPayload(): Payload {
  return {
    version_schema: 1,
    meta: { type: "maison", construction_year: 1980, shab_m2: 100, inertia: "moyenne" },
    walls: [],
    windows: [],
    doors: [],
    heating: [{ generator: "", share: 1 }],
    ecs: { generator: "" },
    ventilation: { type: "", has_joints: true, has_trappe_cheminee: true },
    cooling: {
      percent_climatized: 0,
      energy: "electricite",
      is_top_floor: false,
      // V0.9.14 — defaults safe : None / false laissent l'arbre confort d'été calculer
      protection_solaire: null,
      ventilation_traversante: false,
      brasseur_air: false,
    },
    renewable: { pv_capteurs_m2: 0 },
  };
}

function upgradeInsulationYear<T extends { insulation_year?: InsulationYear | null }>(
  item: T | undefined,
  constructionYear: number,
): T | undefined {
  if (!item || item.insulation_year !== "from_2006" || constructionYear < 2013) return item;
  return { ...item, insulation_year: "from_2013" };
}

function normalizeLoadedPayload(raw: Payload): Payload {
  const year = raw.meta?.construction_year ?? 0;
  const floor = raw.low_floor
    ? {
        ...upgradeInsulationYear(raw.low_floor, year)!,
        adjacent:
          raw.low_floor.adjacent === "veranda_non_chauffee"
            ? "veranda"
            : raw.low_floor.adjacent,
      }
    : raw.low_floor;
  return {
    ...raw,
    walls: (raw.walls ?? []).map((w) => {
      const upgraded = upgradeInsulationYear(w, year)!;
      if (!upgraded.insulation_enduit_isolant) return upgraded;
      return { ...upgraded, isolation_type: null, insulation_thickness_cm: null };
    }),
    roof: upgradeInsulationYear(raw.roof, year),
    low_floor: floor,
  };
}

const INSULATION_YEAR_OPTIONS = [
  { key: "", label: "(non renseignée)" },
  { key: "before_1975", label: "Avant 1975 (peu ou pas d'isolation)" },
  { key: "1975_1977", label: "1975 – 1977" },
  { key: "1978_1982", label: "1978 – 1982" },
  { key: "1983_1988", label: "1983 – 1988" },
  { key: "1989_2000", label: "1989 – 2000" },
  { key: "2001_2005", label: "2001 – 2005 (réglementation thermique 2000)" },
  { key: "from_2006", label: "2006 – 2012 (réglementation thermique 2005)" },
  { key: "from_2013", label: "2013 et après (réglementation thermique 2012)" },
];

const ISOLATION_TYPE_OPTIONS = [
  { key: "", label: "(non renseigné)" },
  { key: "ITI", label: "Isolation par l'intérieur (ITI) — doublage placo" },
  { key: "ITE", label: "Isolation par l'extérieur (ITE) — bardage / enduit" },
  { key: "ITR", label: "Isolation répartie (ITR) — béton cellulaire, brique monomur" },
  { key: "ITI+ITE", label: "Mixte intérieur + extérieur (ITI + ITE)" },
];

const ROOF_ISOLATION_TYPE_OPTIONS = [
  { key: "", label: "(non renseigné)" },
  { key: "ITI", label: "Isolation par l'intérieur (ITI) — sous rampants ou plafond" },
  { key: "ITE", label: "Isolation par l'extérieur (ITE) — sarking, au-dessus des chevrons" },
  { key: "ITR", label: "Isolation répartie (ITR) — dans la dalle, toit-terrasse" },
];

const LOW_FLOOR_ISOLATION_TYPE_OPTIONS = [
  { key: "", label: "(non renseigné)" },
  { key: "ITI", label: "Isolation par l'intérieur (ITI) — chape rapportée par-dessus" },
  { key: "ITE", label: "Isolation par l'extérieur (ITE) — sous-face, par dessous" },
  { key: "ITR", label: "Isolation répartie (ITR) — dans la dalle" },
  { key: "Inconnu", label: "Inconnu (forfait, géométrie immeuble si saisie)" },
];

const INERTIA_OPTIONS = [
  { key: "", label: "(déduction automatique)" },
  { key: "legere", label: "Légère (ossature bois, cloisons placo)" },
  { key: "moyenne", label: "Moyenne (typique maçonnerie classique)" },
  { key: "lourde", label: "Lourde (murs pierre / béton, dalle lourde)" },
  { key: "tres_lourde", label: "Très lourde (pierre ancienne, terre, pisé)" },
];

const B_STATE_OPTIONS = [
  { key: "", label: "(non renseigné — forfait b)" },
  { key: "isole", label: "Isolé" },
  { key: "non_isole", label: "Non isolé" },
];

const ROOF_ADJACENT_OPTIONS = [
  { key: "exterieur", label: "Extérieur" },
  { key: "local_non_chauffe", label: "Local non chauffé" },
  { key: "combles_perdus_ventiles", label: "Combles perdus ventilés" },
  { key: "combles_perdus_non_ventiles", label: "Combles perdus non ventilés" },
  { key: "garage_attenant", label: "Garage attenant" },
  { key: "batiment_adjacent", label: "Bâtiment adjacent" },
];

const FLOOR_ADJACENT_OPTIONS = [
  { key: "local_non_chauffe", label: "Local non chauffé" },
  { key: "terre_plein", label: "Terre-plein" },
  { key: "vide_sanitaire", label: "Vide sanitaire" },
  { key: "sous_sol_non_chauffe", label: "Sous-sol non chauffé" },
  { key: "enterre", label: "Enterré" },
  { key: "garage_attenant", label: "Garage attenant" },
  { key: "exterieur", label: "Extérieur" },
  { key: "circulation_commune", label: "Circulation commune" },
  { key: "local_chauffe", label: "Local chauffé (logement voisin)" },
  { key: "veranda", label: "Véranda / espace tampon" },
];

const LNC_ADJACENTS = new Set([
  "local_non_chauffe",
  "garage_attenant",
  "circulation_commune",
  "vide_sanitaire",
  "sous_sol_non_chauffe",
  "combles_perdus_ventiles",
  "combles_perdus_non_ventiles",
  "veranda",
  "veranda_non_chauffee",
  "enterre",
]);

const BAIE_INCLINAISON_OPTIONS = [
  { key: "verticale", label: "Verticale (fenêtre standard)" },
  { key: "25_75", label: "Inclinée 25° à 75° (versant de toit)" },
  { key: "moins_25", label: "Quasi-horizontale (< 25°, verrière plate)" },
];

const MENUISERIE_TYPE_OPTIONS = [
  { key: "pvc", label: "PVC" },
  { key: "bois", label: "Bois" },
  { key: "metal_rpt", label: "Métal avec rupture de pont thermique" },
  { key: "metal_sans_rpt", label: "Métal sans rupture de pont thermique" },
];

const TYPE_FENETRE_OPTIONS = [
  { key: "battante", label: "Battante (ouvrant à la française)" },
  { key: "coulissante", label: "Coulissante" },
  { key: "porte_fenetre", label: "Porte-fenêtre" },
  { key: "porte_fenetre_soubassement", label: "Porte-fenêtre avec soubassement" },
];

const POSITION_MENUISERIE_OPTIONS = [
  { key: "tunnel", label: "Tunnel (entre les deux nus du mur)" },
  { key: "nu_exterieur", label: "Au nu extérieur (côté façade)" },
  { key: "nu_interieur", label: "Au nu intérieur (côté pièce)" },
];

const LARGEUR_DORMANT_OPTIONS = [
  { key: "", label: "(non renseignée)" },
  { key: "5", label: "5 cm" },
  { key: "10", label: "10 cm" },
];

const INTERMITTENCE_OPTIONS = [
  { key: "", label: "(automatique : inféré selon régulation)" },
  { key: "absent", label: "Aucun équipement d'intermittence" },
  { key: "central_sans_min", label: "Central sans minimum de température" },
  { key: "central_avec_min", label: "Central avec minimum de température" },
  { key: "piece_avec_min", label: "Par pièce avec minimum de température" },
];

const ALIMENTATION_CET_OPTIONS = [
  { key: "", label: "(non renseigné)" },
  { key: "permanente", label: "Permanente (24 h/24)" },
  { key: "heure_creuse", label: "Asservie aux heures creuses" },
];

// 3CL § 8.1 — émetteur de chauffage (clé heating_rendements.Re_by_emitter).
const EMITTER_OPTIONS = [
  { key: "radiateur_eau_chaude", label: "Radiateur à eau chaude" },
  { key: "plancher_chauffant", label: "Plancher chauffant (eau)" },
  { key: "convecteur_electrique", label: "Convecteur électrique" },
  { key: "panneau_rayonnant_electrique", label: "Panneau rayonnant électrique" },
  { key: "radiateur_electrique_inertie", label: "Radiateur électrique à inertie" },
  { key: "radiateur_electrique_accumulation", label: "Radiateur électrique à accumulation" },
  { key: "plafond_rayonnant", label: "Plafond rayonnant" },
  { key: "soufflage_air_chaud", label: "Soufflage d'air chaud" },
  { key: "poele", label: "Poêle / insert" },
];

// 3CL § 8.2 — distribution du chauffage (clé heating_rendements.Rd_by_distribution).
// « pas_de_reseau » = électrique direct / PAC air-air (Rd = 1.0).
const DISTRIBUTION_OPTIONS = [
  { key: "pas_de_reseau", label: "Pas de réseau (électrique direct, pompe à chaleur air/air)" },
  { key: "individuel_eau_basse_temperature", label: "Individuel eau basse température" },
  { key: "individuel_eau_haute_temperature", label: "Individuel eau haute température" },
  { key: "collectif_eau_basse_temperature", label: "Collectif eau basse température" },
  { key: "collectif_eau_haute_temperature", label: "Collectif eau haute température" },
  { key: "reseau_aeraulique", label: "Réseau aéraulique (air pulsé)" },
];

// 3CL § 8.3 — régulation du chauffage (clé heating_rendements.Rr_by_regulation).
// Défaut backend conservateur = sans robinet thermostatique (Rr 0.90).
const REGULATION_OPTIONS = [
  { key: "radiateur_eau_sans_thermostatique", label: "Eau, sans robinet thermostatique (conservateur)" },
  { key: "radiateur_eau_avec_thermostatique", label: "Eau, avec robinet thermostatique" },
  { key: "convecteur_electrique_nfc", label: "Électrique NFC (thermostat électronique)" },
  { key: "radiateur_electrique_nfc", label: "Radiateur électrique NFC" },
  { key: "plancher_rayonnant_electrique_avec_regul_terminale", label: "Plancher rayonnant électrique avec régulation terminale" },
  { key: "plancher_rayonnant_electrique_sans_regul", label: "Plancher rayonnant électrique sans régulation" },
  { key: "plancher_chauffant_eau_individuel", label: "Plancher chauffant eau (individuel)" },
  { key: "plancher_chauffant_eau_collectif", label: "Plancher chauffant eau (collectif)" },
  { key: "chaudiere_electrique", label: "Chaudière électrique" },
  { key: "poele_charbon_bois_fioul_gpl", label: "Poêle (charbon / bois / fioul / GPL)" },
];

// 3CL § 7 — configuration multi-générateurs (base + appoint, etc.).
const HEATING_CONFIG_OPTIONS = [
  { key: "simple", label: "Système unique (un seul générateur)" },
  { key: "avec_appoint_bois", label: "Base + appoint bois (poêle / insert)" },
  { key: "avec_solaire", label: "Base + appoint solaire" },
  { key: "poele_avec_sdb_electrique", label: "Poêle principal + appoint électrique (salle de bain)" },
  { key: "systeme_plus_poele_plus_sdb", label: "Système central + poêle + appoint électrique" },
  { key: "chaudiere_releve_bois", label: "Chaudière en relève d'un poêle bois" },
  { key: "chaudiere_releve_pac", label: "Chaudière en relève d'une pompe à chaleur (PAC)" },
  { key: "pac_chaudiere_avec_appoint_bois", label: "Pompe à chaleur (PAC) + chaudière + appoint bois" },
  { key: "collectif_base_appoint", label: "Collectif base + appoint" },
  { key: "multi_installations", label: "Installations multiples" },
];

// Heuristiques pour conditionner les contrôles ECS avancés sur la clé
// générateur catalogue. Le label est en minuscules pour normalisation.
function isCetGenerator(key: string): boolean {
  const k = (key ?? "").toLowerCase();
  return k.includes("cet") || k.includes("thermodynamique");
}

function isBallonOrCetGenerator(key: string): boolean {
  const k = (key ?? "").toLowerCase();
  return k.includes("ballon") || isCetGenerator(k);
}

function isInstantEcs(key: string): boolean {
  return (key ?? "").toLowerCase().includes("instantane");
}

type BSpec = {
  aiu_aue_ratio?: number | null;
  uvue?: number | null;
  lnc_state?: "isole" | "non_isole" | null;
  paroi_state?: "isole" | "non_isole" | null;
};

function commitB(spec: BSpec, partial: Partial<BSpec>): Partial<BSpec> {
  const next: BSpec = { ...spec, ...partial };
  if (next.lnc_state && next.paroi_state) {
    return {
      ...partial,
      aiu_aue_ratio: next.aiu_aue_ratio ?? 0,
      uvue: next.uvue ?? 0,
    };
  }
  return partial;
}

function BNumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-700">{label}</label>
      <input
        type="number"
        step="any"
        value={value ?? ""}
        placeholder="vide"
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === "") {
            onChange(null);
            return;
          }
          const n = parseFloat(raw);
          onChange(Number.isFinite(n) ? n : null);
        }}
        className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
      />
    </div>
  );
}

function BCoefficientFields({
  spec,
  onChange,
}: {
  spec: BSpec;
  onChange: (partial: Partial<BSpec>) => void;
}) {
  const set = (partial: Partial<BSpec>) => onChange(commitB(spec, partial));
  return (
    <div className="mt-3 rounded border border-dashed border-gray-300 p-3">
      <p className="mb-2 text-xs text-gray-600">
        Coefficient b (table Aiu/Aue). Aue = parois du local non chauffé vers
        l'extérieur. Aiu = paroi entre le logement et ce local. Un champ vide
        n'est pas un zéro : les deux états enregistrent 0 si les nombres sont
        encore vides.
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <BNumberField
          label="Ratio Aiu/Aue"
          value={spec.aiu_aue_ratio}
          onChange={(v) => set({ aiu_aue_ratio: v })}
        />
        <BNumberField
          label="Uv,ue (W/m²·K), 0 admis"
          value={spec.uvue}
          onChange={(v) => set({ uvue: v })}
        />
        <SelectField
          label="Parois du local vers l'extérieur (Aue)"
          value={spec.lnc_state ?? ""}
          onChange={(v) => set({ lnc_state: (v || null) as BSpec["lnc_state"] })}
          options={B_STATE_OPTIONS}
        />
        <SelectField
          label="Paroi logement ↔ local (Aiu)"
          value={spec.paroi_state ?? ""}
          onChange={(v) => set({ paroi_state: (v || null) as BSpec["paroi_state"] })}
          options={B_STATE_OPTIONS}
        />
      </div>
    </div>
  );
}

// Le wizard graphique 2D est désactivé côté UI (option masquée) sans suppression
// du code : passer à `true` pour réactiver le bascule Tabulaire / Wizard 2D.
const WIZARD_ENABLED = false;

// Onglets de saisie (un poste par onglet). `section` relie l'onglet à la clé
// utilisée pour la validation (sectionErrors) → badge "champs manquants".
const SAISIE_TABS = [
  { key: "meta", label: "Logement", section: "meta" },
  { key: "walls", label: "Murs", section: "walls" },
  { key: "roof", label: "Toiture", section: "roof" },
  { key: "low_floor", label: "Plancher bas", section: "low_floor" },
  { key: "windows", label: "Fenêtres", section: "windows" },
  { key: "doors", label: "Portes", section: "doors" },
  { key: "systems", label: "Systèmes", section: "systems" },
  { key: "resultat", label: "Résultat", section: "resultat" },
] as const;

// Sous-onglets de l'onglet "Résultat".
const RESULT_SUBTABS = [
  { key: "synthese", label: "Synthèse" },
  { key: "estimation", label: "Simulation DPE + détails" },
  { key: "conseils", label: "Améliorer sans travaux" },
  { key: "travaux", label: "Simulation de travaux" },
  { key: "action", label: "Passer à l'action" },
] as const;

// =============================================================================
// Page
// =============================================================================

export default function LogementDPEPage() {
  const { id = "" } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const wizardMode = WIZARD_ENABLED && searchParams.get("mode") === "wizard";
  const { data: logement } = useLogement(id);
  const { data: dpe, isLoading: dpeLoading } = useDpe(id);
  const { data: catalogs, isLoading: catalogsLoading } = useCatalogs();
  const calcMutation = useCalculateDpe(id);
  // Données de la vue synthèse (mêmes que /resultat) — chargées une fois le
  // calcul disponible, pour un affichage inline dans le sous-onglet Synthèse.
  const { data: reportData } = useQuery({
    queryKey: ["report-data", id],
    queryFn: () => getReportData(id),
    enabled: !!id && !!dpe?.result,
  });

  const [payload, setPayload] = useState<Payload>(emptyPayload());
  const [serverError, setServerError] = useState<string | null>(null);
  // Champs obligatoires manquants par section (rempli après un calcul refusé
  // en 422 INVALID_PAYLOAD) — sert à mettre en lumière les sections fautives.
  const [sectionErrors, setSectionErrors] = useState<SectionErrors>({});
  const [planGeometry, setPlanGeometry] = useState<PlanGeometry>(emptyPlan());
  const [stepIndex, setStepIndex] = useState(0);
  // Navigation par onglets (mode par défaut). `showAll` bascule vers la vue
  // « tout sur une page ». L'autosave est global : changer d'onglet ne perd rien.
  const [activeTab, setActiveTab] = useState(() => {
    // Ouverture sur un onglet précis via ?tab=<clé> (ex. lien « Voir mon
    // estimation » → ?tab=resultat).
    const i = SAISIE_TABS.findIndex((t) => t.key === searchParams.get("tab"));
    return i >= 0 ? i : 0;
  });
  const [showAll, setShowAll] = useState(false);
  const [resultSubTab, setResultSubTab] = useState<string>("synthese");
  // Drapeaux admin (publiques, cache 5 min) : masque le sous-onglet
  // « Simulation DPE + détails » si désactivé via /admin/flags.
  const { data: uiFlags } = useQuery({
    queryKey: ["ui-flags"],
    queryFn: getUiFlags,
    staleTime: 5 * 60 * 1000,
  });
  const visibleResultSubTabs = RESULT_SUBTABS.filter(
    (st) => !(st.key === "estimation" && uiFlags?.show_simulation_details_tab === false),
  );
  // Vrai une fois le payload initial chargé depuis le serveur. L'autosave n'est
  // armé qu'ensuite, pour qu'une simple hydratation ne déclenche pas un PATCH
  // (qui invaliderait inutilement le résultat calculé).
  const [hydrated, setHydrated] = useState(false);

  // Auto-save debounced (RG-SAIS-01) — 1.5s d'inactivité avant PATCH
  const { status: autosaveStatus, flushNow } = useDebouncedAutosave(
    payload,
    async (value) => {
      await updateDpePayload(id, value as unknown as Record<string, unknown>);
    },
    {
      delayMs: 1500,
      enabled: hydrated,
      // Flush de dernière chance à la fermeture de l'onglet : PATCH keepalive
      // (fire-and-forget) qui survit à la navigation/unload.
      onUnloadSave: (value) => {
        void updateDpePayload(
          id,
          value as unknown as Record<string, unknown>,
          { keepalive: true },
        ).catch(() => {
          /* best-effort : rien à afficher, la page se ferme */
        });
      },
    },
  );

  // Helper pour récupérer un tutoriel par sa clé (F-SAIS-019)
  const tutorialFor = (key: string): TutorialOption | undefined =>
    catalogs?.tutorials?.find((t) => t.key === key);

  // Charger le payload existant à l'arrivée
  useEffect(() => {
    if (dpe?.payload && typeof dpe.payload === "object" && Object.keys(dpe.payload).length > 0) {
      try {
        setPayload(normalizeLoadedPayload({ ...emptyPayload(), ...(dpe.payload as Payload) }));
      } catch {
        // ignore et garde le payload par défaut
      }
      setHydrated(true);
    } else if (logement) {
      // Département : on prend celui stocké sur le logement, sinon on le
      // dérive du code postal côté frontend (cas legacy sans dept_code).
      const derivedDept =
        logement.department_code ?? postalToDepartment(logement.postal_code);
      setPayload((p) => ({
        ...p,
        meta: {
          ...p.meta,
          type: logement.type,
          construction_year: logement.construction_year ?? p.meta.construction_year,
          department_code: derivedDept ?? undefined,
        },
      }));
      setHydrated(true);
    }
  }, [dpe?.payload, logement]);

  // IMPORTANT : tous les hooks doivent être appelés AVANT le moindre early
  // return (Rules of Hooks). useMemo des steps doit donc rester au-dessus du
  // garde `if (dpeLoading || ...) return <Loading/>` plus bas.
  const steps: WizardStep[] = useMemo(() => {
    return [
      {
        key: "meta",
        title: "Informations du logement",
        shortTitle: "Logement",
        complete: !!payload.meta.shab_m2 && payload.meta.construction_year > 0,
      },
      {
        key: "plan",
        title: "Plan 2D",
        shortTitle: "Plan",
        complete: planGeometry.rooms.length > 0,
        intraProgress:
          planGeometry.rooms.length > 0
            ? Math.min(1, planGeometry.rooms.length / 2)
            : 0,
      },
      {
        key: "walls",
        title: "Parois (murs, toit, plancher)",
        shortTitle: "Parois",
        complete: payload.walls.length > 0,
        intraProgress:
          payload.walls.length === 0
            ? 0
            : (payload.walls.filter((w) => !!w.material).length +
                (payload.roof ? 1 : 0) +
                (payload.low_floor ? 1 : 0)) /
              (payload.walls.length + 2),
      },
      {
        key: "openings",
        title: "Fenêtres et portes",
        shortTitle: "Ouvertures",
        complete: payload.windows.length > 0,
      },
      {
        key: "systems",
        title: "Systèmes (chauffage, ECS, ventilation)",
        shortTitle: "Systèmes",
        complete:
          !!payload.heating[0]?.generator &&
          !!payload.ecs.generator &&
          !!payload.ventilation.type,
      },
    ];
  }, [payload, planGeometry]);

  if (dpeLoading || catalogsLoading || !catalogs) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-6 py-12">Chargement…</div>
      </Layout>
    );
  }

  const handleCalculate = async () => {
    setServerError(null);
    setSectionErrors({});
    // Force la sauvegarde immédiate du brouillon, puis déclenche le calcul.
    try {
      await flushNow();
    } catch {
      setServerError("Impossible de sauvegarder avant le calcul.");
      return;
    }
    calcMutation.mutate(undefined, {
      onSuccess: () => setSectionErrors({}),
      onError: (err: unknown) => {
        if (err instanceof ApiError) {
          if (err.code === "INVALID_PAYLOAD") {
            const parsed = parseValidationErrors(err.details);
            setSectionErrors(parsed);
            // En mode onglets, saute au premier onglet contenant une erreur
            // pour que l'utilisateur voie immédiatement quoi corriger.
            const firstBad = SAISIE_TABS.findIndex(
              (t) =>
                (parsed as Record<string, string[] | undefined>)[t.section]
                  ?.length,
            );
            if (firstBad >= 0) setActiveTab(firstBad);
            setServerError(
              countErrors(parsed) > 0
                ? "Des champs obligatoires sont manquants. Les sections concernées sont signalées en rouge ci-dessous."
                : "Le payload est incomplet. Renseignez tous les champs obligatoires.",
            );
          } else {
            setServerError(err.message);
          }
        } else {
          setServerError("Erreur inattendue.");
        }
      },
    });
  };

  const onPrev = () => setStepIndex((i) => Math.max(0, i - 1));
  const onNext = () => setStepIndex((i) => Math.min(steps.length - 1, i + 1));
  const isLastStep = stepIndex === steps.length - 1;

  // --- Navigation par onglets ---
  const lastTab = SAISIE_TABS.length - 1;
  const onPrevTab = () => setActiveTab((i) => Math.max(0, i - 1));
  const onNextTab = () => setActiveTab((i) => Math.min(lastTab, i + 1));
  const isLastTab = activeTab === lastTab;
  const errorCountFor = (section: string) =>
    (sectionErrors as Record<string, string[] | undefined>)[section]?.length ?? 0;

  // Rendu d'un poste de saisie par sa clé — partagé entre la vue onglets et la
  // vue « tout afficher » pour éviter toute duplication.
  const renderSection = (key: string) => {
    switch (key) {
      case "meta":
        return (
          <MetaSection
            payload={payload}
            setPayload={setPayload}
            tutorial={tutorialFor("meta")}
            errors={sectionErrors.meta}
          />
        );
      case "walls":
        return (
          <WallsSection
            payload={payload}
            setPayload={setPayload}
            catalogs={catalogs}
            tutorial={tutorialFor("walls")}
            errors={sectionErrors.walls}
          />
        );
      case "roof":
        return (
          <RoofSection
            payload={payload}
            setPayload={setPayload}
            catalogs={catalogs}
            tutorial={tutorialFor("roof")}
            errors={sectionErrors.roof}
          />
        );
      case "low_floor":
        return (
          <LowFloorSection
            payload={payload}
            setPayload={setPayload}
            catalogs={catalogs}
            tutorial={tutorialFor("low_floor")}
            errors={sectionErrors.low_floor}
          />
        );
      case "windows":
        return (
          <WindowsSection
            payload={payload}
            setPayload={setPayload}
            catalogs={catalogs}
            tutorial={tutorialFor("windows")}
            errors={sectionErrors.windows}
          />
        );
      case "doors":
        return (
          <DoorsSection
            payload={payload}
            setPayload={setPayload}
            catalogs={catalogs}
            tutorial={tutorialFor("doors")}
            errors={sectionErrors.doors}
          />
        );
      case "systems":
        return (
          <SystemsSection
            payload={payload}
            setPayload={setPayload}
            catalogs={catalogs}
            tutorial={tutorialFor("systems")}
            errors={sectionErrors.systems}
          />
        );
      case "resultat":
        return (
          <section className="rounded border border-gray-200 bg-white p-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold">Résultat du DPE</h2>
                <p className="mt-1 text-sm text-gray-600">
                  Lancez le calcul pour obtenir l'estimation complète (étiquette,
                  coûts, déperditions, projection travaux).
                </p>
              </div>
              <button
                type="button"
                onClick={() => void handleCalculate()}
                disabled={calcMutation.isPending}
                className="rounded bg-blue-900 px-5 py-2.5 font-medium text-white hover:bg-blue-800 disabled:opacity-50"
              >
                {calcMutation.isPending
                  ? "Calcul…"
                  : dpe?.result
                    ? "Recalculer mon DPE"
                    : "Calculer mon DPE"}
              </button>
            </div>
            {calcMutation.isPending && (
              <CalcProgressBar progress={calcMutation.progress} />
            )}
            {dpe?.result ? (
              <div className="mt-6">
                {/* Sous-onglets du résultat */}
                <div
                  role="tablist"
                  aria-label="Sections du résultat"
                  className="flex flex-wrap gap-1 border-b border-gray-200"
                >
                  {visibleResultSubTabs.map((st) => {
                    const active = st.key === resultSubTab;
                    return (
                      <button
                        key={st.key}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => setResultSubTab(st.key)}
                        className={`-mb-px rounded-t border-b-2 px-3 py-2 text-sm ${
                          active
                            ? "border-blue-900 font-semibold text-blue-900"
                            : "border-transparent text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        {st.label}
                      </button>
                    );
                  })}
                </div>

                <div role="tabpanel" className="mt-6 space-y-8">
                  {resultSubTab === "synthese" &&
                    (reportData ? (
                      <>
                        <DpeResultScreen
                          data={reportData}
                          logementId={id}
                          embedded
                          onRefineScenarios={() => setResultSubTab("travaux")}
                        />
                        {/* D17 — Répartition immeuble → appartements */}
                        {(reportData.repartition_appartements?.length ?? 0) >
                          0 && (
                          <div className="rounded border border-gray-200 bg-white p-4">
                            <h3 className="mb-1 text-sm font-semibold text-gray-900">
                              Répartition par logement (§ 17.2.2)
                            </h3>
                            <p className="mb-3 text-xs text-gray-600">
                              Consommations immeuble réparties par logement —
                              chauffage au prorata des surfaces (ou de la clé de
                              répartition si saisie), ECS / éclairage /
                              auxiliaires au prorata des surfaces.
                            </p>
                            <table className="w-full text-xs">
                              <thead>
                                <tr className="text-left text-gray-600">
                                  <th className="py-1 font-medium">Logement</th>
                                  <th className="py-1 font-medium">
                                    Surface (m²)
                                  </th>
                                  <th className="py-1 font-medium">
                                    Chauffage (kWh/an)
                                  </th>
                                  <th className="py-1 font-medium">
                                    ECS (kWh/an)
                                  </th>
                                  <th className="py-1 font-medium">
                                    Éclairage (kWh/an)
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {reportData.repartition_appartements!.map(
                                  (row) => (
                                    <tr
                                      key={row.nom}
                                      className="border-t border-gray-100"
                                    >
                                      <td className="py-1 pr-2">{row.nom}</td>
                                      <td className="py-1">{row.shab_m2}</td>
                                      <td className="py-1">
                                        {Math.round(row.Cch)}
                                      </td>
                                      <td className="py-1">
                                        {Math.round(row.Cecs ?? 0)}
                                      </td>
                                      <td className="py-1">
                                        {Math.round(row.Cecl ?? 0)}
                                      </td>
                                    </tr>
                                  ),
                                )}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">Chargement de la synthèse…</p>
                    ))}
                  {resultSubTab === "estimation" && <ReportView logementId={id} />}
                  {resultSubTab === "conseils" &&
                    (reportData ? (
                      <ConseilsSansTravaux d={reportData} />
                    ) : (
                      <p className="text-sm text-gray-500">Chargement des conseils…</p>
                    ))}
                  {resultSubTab === "travaux" && (
                    <ScenariosPanelGate
                      logementId={id}
                      currentLabel={dpe.result.energy_label}
                    />
                  )}
                  {resultSubTab === "action" && <PasserALActionPanel />}
                </div>
              </div>
            ) : (
              <p className="mt-6 text-sm text-gray-500">
                Aucun résultat pour le moment. Renseignez les onglets précédents,
                puis cliquez sur « Calculer mon DPE ».
              </p>
            )}
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-4xl px-6 py-12">
        <Link to={`/logements/${id}`} className="text-sm text-blue-900 underline">
          ← Retour au logement
        </Link>
        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold">Saisie DPE</h1>
            <p className="mt-1 text-sm text-gray-600">
              {wizardMode
                ? "Wizard pas-à-pas avec plan 2D."
                : showAll
                  ? "Saisie sur une seule page."
                  : "Saisie par onglets — naviguez librement, tout est sauvegardé automatiquement."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {!wizardMode && (
              <button
                type="button"
                onClick={() => setShowAll((v) => !v)}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100"
              >
                {showAll ? "Vue par onglets" : "Tout afficher"}
              </button>
            )}
            {WIZARD_ENABLED && (
              <ModeToggle
                wizard={wizardMode}
                onChange={(w) => {
                  setSearchParams(w ? { mode: "wizard" } : {});
                  setStepIndex(0);
                }}
              />
            )}
          </div>
        </div>

        <div className="mt-3">
          <AutosaveIndicator status={autosaveStatus} />
        </div>

        {wizardMode ? (
          <>
            <WizardStepper
              steps={steps}
              currentIndex={stepIndex}
              onSelect={setStepIndex}
            />
            <div className="mt-6 space-y-8">
              {stepIndex === 0 && renderSection("meta")}
              {stepIndex === 1 && (
                <PlanStep
                  geometry={planGeometry}
                  onChange={setPlanGeometry}
                  tutorial={tutorialFor("walls")}
                  onApplyToWalls={(surfaceM2) => {
                    // Pré-remplit la liste des murs avec un mur unique sur la
                    // surface totale calculée. L'utilisateur affinera ensuite.
                    if (surfaceM2 > 0) {
                      setPayload((p) => ({
                        ...p,
                        walls:
                          p.walls.length > 0
                            ? p.walls
                            : [{ material: "", surface_m2: surfaceM2, adjacent: "exterieur" }],
                      }));
                    }
                  }}
                />
              )}
              {stepIndex === 2 && (
                <>
                  {renderSection("walls")}
                  {renderSection("roof")}
                  {renderSection("low_floor")}
                </>
              )}
              {stepIndex === 3 && (
                <>
                  {renderSection("windows")}
                  {renderSection("doors")}
                </>
              )}
              {stepIndex === 4 && renderSection("systems")}
            </div>
          </>
        ) : showAll ? (
          <div className="mt-6 space-y-8">
            {SAISIE_TABS.map((t) => (
              <div key={t.key}>{renderSection(t.key)}</div>
            ))}
          </div>
        ) : (
          <>
            <div
              role="tablist"
              aria-label="Sections de saisie"
              className="mt-4 flex flex-wrap gap-1 border-b border-gray-200"
            >
              {SAISIE_TABS.map((t, i) => {
                const n = errorCountFor(t.section);
                const active = i === activeTab;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setActiveTab(i)}
                    className={`-mb-px flex items-center gap-1.5 rounded-t border-b-2 px-3 py-2 text-sm ${
                      active
                        ? "border-blue-900 font-semibold text-blue-900"
                        : "border-transparent text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {t.label}
                    {n > 0 && (
                      <span className="rounded-full bg-red-100 px-1.5 text-xs font-semibold text-red-700">
                        {n}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            <div role="tabpanel" className="mt-6">
              {renderSection(SAISIE_TABS[activeTab].key)}
            </div>
          </>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-3">
          {/* Wizard (désactivé) : navigation pas-à-pas */}
          {wizardMode && stepIndex > 0 && (
            <button
              type="button"
              onClick={onPrev}
              className="rounded border border-gray-300 px-4 py-2 hover:bg-gray-100"
            >
              ← Précédent
            </button>
          )}
          {wizardMode && !isLastStep && (
            <button
              type="button"
              onClick={onNext}
              className="rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800"
            >
              Suivant →
            </button>
          )}
          {/* Onglets : navigation Précédent / Suivant entre les postes */}
          {!wizardMode && !showAll && activeTab > 0 && (
            <button
              type="button"
              onClick={onPrevTab}
              className="rounded border border-gray-300 px-4 py-2 hover:bg-gray-100"
            >
              ← Précédent
            </button>
          )}
          {!wizardMode && !showAll && !isLastTab && (
            <button
              type="button"
              onClick={onNextTab}
              className="rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800"
            >
              Suivant →
            </button>
          )}
          {/* Calculer : conservé uniquement pour le wizard (désactivé). En mode
              onglets / tout-afficher, le bouton vit dans l'onglet "Résultat". */}
          {wizardMode && isLastStep && (
            <button
              type="button"
              onClick={() => void handleCalculate()}
              disabled={calcMutation.isPending}
              className="rounded bg-blue-900 px-4 py-2 text-white hover:bg-blue-800 disabled:opacity-50"
            >
              {calcMutation.isPending ? "Calcul…" : "Calculer mon DPE"}
            </button>
          )}
          <AutosaveIndicator status={autosaveStatus} />
          {calcMutation.isPending && (
            <div className="w-full">
              <CalcProgressBar progress={calcMutation.progress} />
            </div>
          )}
          {serverError && (
            <p role="alert" className="w-full text-sm text-red-700">
              {serverError}
            </p>
          )}
        </div>
      </div>
    </Layout>
  );
}

// =============================================================================
// Barre de progression du calcul (streaming SSE)
// =============================================================================

function CalcProgressBar({ progress }: { progress: CalculateProgressState }) {
  const pct = progress?.pct ?? 0;
  const label = progress?.label ?? "Initialisation…";
  return (
    <div
      role="progressbar"
      aria-label="Progression du calcul DPE"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-300">
        <span>{label}</span>
        <span className="tabular-nums font-semibold">{pct}%</span>
      </div>
      <div className="mt-1 h-2.5 w-full overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
        <div
          className="h-full rounded bg-blue-900 transition-all duration-300 dark:bg-blue-500"
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

// =============================================================================
// Toggle wizard / tabular
// =============================================================================

function ModeToggle({
  wizard,
  onChange,
}: {
  wizard: boolean;
  onChange: (wizard: boolean) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Mode de saisie"
      className="inline-flex rounded border border-gray-300 bg-white text-sm"
    >
      <button
        type="button"
        role="radio"
        aria-checked={!wizard}
        onClick={() => onChange(false)}
        className={`px-3 py-1.5 ${
          !wizard ? "bg-blue-900 text-white" : "text-gray-700"
        }`}
      >
        Tabulaire
      </button>
      <button
        type="button"
        role="radio"
        aria-checked={wizard}
        onClick={() => onChange(true)}
        className={`px-3 py-1.5 ${
          wizard ? "bg-blue-900 text-white" : "text-gray-700"
        }`}
      >
        Wizard 2D
      </button>
    </div>
  );
}

// =============================================================================
// Step 2 wizard : plan 2D Konva
// =============================================================================

function PlanStep({
  geometry,
  onChange,
  tutorial,
  onApplyToWalls,
}: {
  geometry: PlanGeometry;
  onChange: (next: PlanGeometry) => void;
  tutorial?: TutorialOption;
  onApplyToWalls: (surfaceM2: number) => void;
}) {
  const derived = geometryToWallSurface(geometry, 2.5);
  return (
    <SectionShell title="Plan du logement (2D)" tutorial={tutorial}>
      <p className="text-sm text-gray-600">
        Dessinez chaque pièce de votre logement en cliquer-glissant. La surface
        de murs extérieurs sera calculée automatiquement (basée sur le périmètre
        × 2,5 m de hauteur sous plafond).
      </p>
      <WizardPlanCanvas value={geometry} onChange={onChange} />
      {geometry.rooms.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <p className="text-sm">
            Surface de murs calculée :{" "}
            <strong>{derived.total_wall_surface_m2} m²</strong>
          </p>
          <button
            type="button"
            onClick={() => onApplyToWalls(derived.total_wall_surface_m2)}
            className="rounded bg-blue-900 px-3 py-1.5 text-sm text-white hover:bg-blue-800"
          >
            Appliquer aux murs (étape suivante)
          </button>
        </div>
      )}
    </SectionShell>
  );
}

// =============================================================================
// Sections (composants présentationnels)
// =============================================================================

type SectionProps = {
  payload: Payload;
  setPayload: React.Dispatch<React.SetStateAction<Payload>>;
  catalogs: Catalogs;
  tutorial?: TutorialOption;
  /** Champs obligatoires manquants à signaler dans cette section. */
  errors?: string[];
};

function MetaSection({
  payload,
  setPayload,
  tutorial,
  errors,
}: Omit<SectionProps, "catalogs">) {
  return (
    <SectionShell title="Informations générales" tutorial={tutorial} errors={errors}>
      <div className="grid grid-cols-2 gap-3">
        <NumberField
          label="Surface habitable (m²)"
          value={payload.meta.shab_m2}
          onChange={(v) =>
            setPayload({ ...payload, meta: { ...payload.meta, shab_m2: v } })
          }
        />
        <NumberField
          label="Année de construction"
          value={payload.meta.construction_year}
          onChange={(v) =>
            setPayload(normalizeLoadedPayload({
              ...payload,
              meta: { ...payload.meta, construction_year: v },
            }))
          }
        />
        <div>
          <label className="mb-1 block text-sm font-medium">
            Département
          </label>
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
            {payload.meta.department_code ? (
              <span>
                <strong>{payload.meta.department_code}</strong>{" "}
                <span className="text-xs text-gray-500">
                  (déduit du code postal du logement)
                </span>
              </span>
            ) : (
              <span className="text-xs text-gray-500">
                Non détecté. Renseignez le code postal de votre logement
                dans la fiche logement.
              </span>
            )}
          </div>
        </div>
        <SelectField
          label="Altitude"
          value={payload.meta.altitude_band ?? ""}
          onChange={(v) =>
            setPayload({
              ...payload,
              meta: {
                ...payload.meta,
                altitude_band: (v || undefined) as Payload["meta"]["altitude_band"],
              },
            })
          }
          options={[
            { key: "", label: "(non renseigné)" },
            { key: "<=400", label: "Jusqu'à 400 m" },
            { key: "401-800", label: "401 à 800 m" },
            { key: ">800", label: "Plus de 800 m" },
          ]}
        />
        <SelectField
          label="Inertie thermique"
          value={payload.meta.inertia ?? ""}
          onChange={(v) =>
            setPayload({
              ...payload,
              meta: {
                ...payload.meta,
                inertia: (v || undefined) as Payload["meta"]["inertia"],
              },
            })
          }
          options={INERTIA_OPTIONS}
        />
        <SelectField
          label="Parois anciennes (§ 18.3)"
          value={
            payload.meta.parois_anciennes == null
              ? ""
              : payload.meta.parois_anciennes
                ? "1"
                : "0"
          }
          onChange={(v) =>
            setPayload({
              ...payload,
              meta: {
                ...payload.meta,
                parois_anciennes: v === "" ? null : v === "1",
              },
            })
          }
          options={[
            { key: "", label: "(déduction si inertie lourde)" },
            { key: "1", label: "Oui — tables climatiques parois anciennes" },
            { key: "0", label: "Non — tables standard" },
          ]}
        />
      </div>

      {/* D17 — Layout bâtiment (§ 17.2.2) : saisie des logements de
          l'immeuble → meta.appartements → répartition Cch/Cecs/aux (méthode
          1 prorata surface, ou méthode 2 avec clé de répartition). */}
        {payload.meta.type === "appartement" && (
          <LayoutBatimentSection
            payload={payload}
            setPayload={setPayload}
          />
        )}
    </SectionShell>
  );
}


// =============================================================================
// D17 — Layout bâtiment : saisie des logements de l'immeuble (§ 17.2.2)
// =============================================================================

type AppartementRow = { nom: string; shab_m2: number; cle_kwh?: number | null };

function LayoutBatimentSection({
  payload,
  setPayload,
}: {
  payload: Payload;
  setPayload: React.Dispatch<React.SetStateAction<Payload>>;
}) {
  const appartements: AppartementRow[] = payload.meta.appartements ?? [];

  const setAppartements = (rows: AppartementRow[]) =>
    setPayload({
      ...payload,
      meta: { ...payload.meta, appartements: rows },
    });

  const updateRow = (i: number, patch: Partial<AppartementRow>) =>
    setAppartements(
      appartements.map((row, j) => (j === i ? { ...row, ...patch } : row)),
    );

  const addRow = () =>
    setAppartements([
      ...appartements,
      {
        nom: `Appartement ${appartements.length + 1}`,
        shab_m2: Math.max(
          0,
          Math.round(
            (payload.meta.shab_m2 ?? 0) /
              Math.max(1, (payload.meta.appartements?.length ?? 0) + 1),
          ),
        ),
        cle_kwh: null,
      },
    ]);

  const removeRow = (i: number) =>
    setAppartements(appartements.filter((_, j) => j !== i));

  const totalSh = appartements.reduce(
    (s, a) => s + (Number.isFinite(a.shab_m2) ? a.shab_m2 : 0),
    0,
  );
  const coherent =
    totalSh > 0 &&
    Math.abs(totalSh - (payload.meta.shab_m2 ?? 0)) <
      0.5 * Math.max(1, appartements.length);

  return (
    <div className="mt-4 space-y-2 rounded border border-gray-100 bg-gray-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-gray-800">
          Layout de l'immeuble (répartition des consommations par logement —
          § 17.2.2)
        </h4>
        <button
          type="button"
          onClick={addRow}
          className="rounded bg-blue-900 px-3 py-1.5 text-xs text-white hover:bg-blue-800"
        >
          + Ajouter un logement
        </button>
      </div>
      <p className="text-xs text-gray-600">
        Décrivez chaque appartement (nom et surface habitable). Optionnel : une
        clé de répartition (kWh de besoins simplifiés) active la méthode 2 de
        l'arrêté — sinon la répartition se fait au prorata des surfaces.
      </p>
      {appartements.length === 0 && (
        <p className="text-xs text-gray-500">
          Aucun logement saisi : les consommations restent à l'échelle de
          l'immeuble. Ajoutez au moins deux logements pour la répartition.
        </p>
      )}
      {appartements.length > 0 && (
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left text-gray-600">
              <th className="py-1 font-medium">Nom</th>
              <th className="py-1 font-medium">Surface habitable (m²)</th>
              <th className="py-1 font-medium">
                Clé de répartition (kWh, optionnel)
              </th>
              <th aria-label="Supprimer" />
            </tr>
          </thead>
          <tbody>
            {appartements.map((row, i) => (
              <tr key={i} className="border-t border-gray-100">
                <td className="py-1 pr-2">
                  <input
                    type="text"
                    value={row.nom}
                    onChange={(e) => updateRow(i, { nom: e.target.value })}
                    className="w-full rounded border border-gray-300 px-2 py-1"
                    aria-label={`Nom du logement ${i + 1}`}
                  />
                </td>
                <td className="py-1">
                  <input
                    type="number"
                    value={row.shab_m2}
                    step="any"
                    onChange={(e) =>
                      updateRow(i, { shab_m2: parseFloat(e.target.value) || 0 })
                    }
                    className="w-full rounded border border-gray-300 px-2 py-1"
                    aria-label={`Surface du logement ${i + 1}`}
                  />
                </td>
                <td className="py-1">
                  <input
                    type="number"
                    value={row.cle_kwh ?? ""}
                    step="any"
                    onChange={(e) =>
                      updateRow(i, {
                        cle_kwh: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      })
                    }
                    className="w-full rounded border border-gray-300 px-2 py-1"
                    aria-label={`Clé de répartition du logement ${i + 1}`}
                  />
                </td>
                <td className="py-1 text-right">
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    aria-label={`Supprimer le logement ${i + 1}`}
                    className="rounded px-2 py-1 text-red-600 hover:bg-red-50"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-gray-200">
              <td className="py-1 font-medium">Total</td>
              <td
                className={`py-1 font-medium ${
                  coherent ? "text-green-700" : "text-red-600"
                }`}
              >
                {totalSh} m²{" "}
                {coherent ? (
                  "(= surface de référence)"
                ) : (
                  <span>
                    (≠ surface de référence {payload.meta.shab_m2} m² — la
                    répartition normalise au prorata)
                  </span>
                )}
              </td>
              <td />
              <td />
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

function WallsSection({ payload, setPayload, catalogs, tutorial, errors }: SectionProps) {
  const addWall = () =>
    setPayload({
      ...payload,
      walls: [...payload.walls, { material: "", surface_m2: 0, adjacent: "exterieur" }],
    });
  const updateWall = (idx: number, partial: Partial<Wall>) =>
    setPayload({
      ...payload,
      walls: payload.walls.map((w, i) => (i === idx ? { ...w, ...partial } : w)),
    });
  const removeWall = (idx: number) =>
    setPayload({ ...payload, walls: payload.walls.filter((_, i) => i !== idx) });

  // V5 — Groupes par famille pour éviter un dropdown de 71 entrées à plat.
  const groupedWalls = useMemo(
    () => groupWallOptions(catalogs.walls),
    [catalogs.walls],
  );

  return (
    <SectionShell
      title="Murs"
      tutorial={tutorial}
      errors={errors}
      onAdd={addWall}
      addLabel="+ Ajouter un mur"
      help="Liste des murs extérieurs du logement, regroupés par famille de matériau."
    >
      {payload.walls.length === 0 && (
        <p className="text-sm text-gray-500">Aucun mur saisi.</p>
      )}
      {payload.walls.map((wall, i) => (
        <div key={i} className="rounded border border-gray-200 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {wall.name?.trim() || `Mur n°${i + 1}`}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <TextField
              label="Nom (optionnel)"
              value={wall.name ?? ""}
              onChange={(v) => updateWall(i, { name: v || null })}
              placeholder="ex : Mur Nord salon"
            />
            <GroupedSelectField
              label="Matériau"
              value={wall.material}
              onChange={(v) => updateWall(i, { material: v })}
              options={groupedWalls}
            />
            <NumberField
              label="Surface (m²)"
              value={wall.surface_m2}
              onChange={(v) => updateWall(i, { surface_m2: v })}
            />
            <SelectField
              label="Donnant sur"
              value={wall.adjacent ?? "exterieur"}
              onChange={(v) => updateWall(i, { adjacent: v })}
              options={catalogs.adjacent_options}
            />
            <SelectField
              label="Année d'isolation"
              value={wall.insulation_year ?? ""}
              onChange={(v) => updateWall(i, { insulation_year: (v || null) as Wall["insulation_year"] })}
              options={INSULATION_YEAR_OPTIONS}
            />
            <SelectField
              label="Type d'isolation"
              value={wall.insulation_enduit_isolant ? "" : (wall.isolation_type ?? "")}
              onChange={(v) =>
                updateWall(i, {
                  isolation_type: (v || null) as Wall["isolation_type"],
                  insulation_enduit_isolant: v ? false : wall.insulation_enduit_isolant,
                })
              }
              options={ISOLATION_TYPE_OPTIONS}
            />
            {wall.isolation_type && !wall.insulation_enduit_isolant && (
              <NumberField
                label="Épaisseur isolant (cm)"
                value={wall.insulation_thickness_cm ?? 0}
                onChange={(v) =>
                  updateWall(i, {
                    insulation_thickness_cm: v > 0 ? v : null,
                    insulation_enduit_isolant: v > 0 ? false : wall.insulation_enduit_isolant,
                  })
                }
              />
            )}
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-gray-300"
                checked={!!wall.insulation_enduit_isolant}
                onChange={(e) =>
                  updateWall(
                    i,
                    e.target.checked
                      ? {
                          insulation_enduit_isolant: true,
                          isolation_type: null,
                          insulation_thickness_cm: null,
                        }
                      : { insulation_enduit_isolant: false },
                  )
                }
              />
              Enduit isolant (R = 0,7, à la place du type et de l'épaisseur)
            </label>
          </div>
          {LNC_ADJACENTS.has(wall.adjacent ?? "") && (
            <BCoefficientFields spec={wall} onChange={(partial) => updateWall(i, partial)} />
          )}
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
              Précision avancée (λ)
            </summary>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <NumberField
                label="Lambda λ (W/m·K)"
                value={wall.insulation_lambda_w_m_k ?? 0.04}
                onChange={(v) => updateWall(i, { insulation_lambda_w_m_k: v })}
              />
            </div>
          </details>
          <div className="mt-2 flex justify-end">
            <RemoveButton onClick={() => removeWall(i)} />
          </div>
        </div>
      ))}
    </SectionShell>
  );
}

function RoofSection({ payload, setPayload, catalogs, tutorial, errors }: SectionProps) {
  const r = payload.roof;
  const set = (next: Roof | undefined) => setPayload({ ...payload, roof: next });

  return (
    <SectionShell title="Toiture / plancher haut" tutorial={tutorial} errors={errors}>
      {!r ? (
        <button
          type="button"
          onClick={() =>
            set({ material: "", surface_m2: 0, insulation: null })
          }
          className="text-sm text-blue-900 underline"
        >
          + Renseigner ma toiture
        </button>
      ) : (
        <div className="rounded border border-gray-200 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {r.name?.trim() || "Toiture"}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <TextField
              label="Nom (optionnel)"
              value={r.name ?? ""}
              onChange={(v) => set({ ...r, name: v || null })}
              placeholder="ex : Toit principal"
            />
            <SelectField
              label="Type"
              value={r.material}
              onChange={(v) => set({ ...r, material: v })}
              options={[{ key: "", label: "(choisir)" }, ...catalogs.roofs]}
            />
            <NumberField
              label="Surface (m²)"
              value={r.surface_m2}
              onChange={(v) => set({ ...r, surface_m2: v })}
            />
            <SelectField
              label="Donnant sur"
              value={r.adjacent ?? "exterieur"}
              onChange={(v) => set({ ...r, adjacent: v })}
              options={ROOF_ADJACENT_OPTIONS}
            />
            <SelectField
              label="Année d'isolation"
              value={r.insulation_year ?? ""}
              onChange={(v) => set({ ...r, insulation_year: (v || null) as Roof["insulation_year"] })}
              options={INSULATION_YEAR_OPTIONS}
            />
            <SelectField
              label="Type d'isolation"
              value={r.isolation_type ?? ""}
              onChange={(v) => set({ ...r, isolation_type: (v || null) as Roof["isolation_type"] })}
              options={ROOF_ISOLATION_TYPE_OPTIONS}
            />
            {r.isolation_type && (
              <NumberField
                label="Épaisseur isolant (cm)"
                value={r.insulation_thickness_cm ?? 0}
                onChange={(v) => set({ ...r, insulation_thickness_cm: v > 0 ? v : null })}
              />
            )}
          </div>
          {LNC_ADJACENTS.has(r.adjacent ?? "") && (
            <BCoefficientFields spec={r} onChange={(partial) => set({ ...r, ...partial })} />
          )}
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
              Précision avancée (λ)
            </summary>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <NumberField
                label="Lambda λ (W/m·K)"
                value={r.insulation_lambda_w_m_k ?? 0.04}
                onChange={(v) => set({ ...r, insulation_lambda_w_m_k: v })}
              />
            </div>
          </details>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => set(undefined)}
              className="text-xs text-red-700 underline"
            >
              Retirer la toiture
            </button>
          </div>
        </div>
      )}
    </SectionShell>
  );
}

function LowFloorSection({ payload, setPayload, catalogs, tutorial, errors }: SectionProps) {
  const f = payload.low_floor;
  const set = (next: LowFloor | undefined) => setPayload({ ...payload, low_floor: next });

  return (
    <SectionShell title="Plancher bas" tutorial={tutorial} errors={errors}>
      {!f ? (
        <button
          type="button"
          onClick={() =>
            set({ material: "", surface_m2: 0, insulation: null, adjacent: "local_non_chauffe" })
          }
          className="text-sm text-blue-900 underline"
        >
          + Renseigner mon plancher bas
        </button>
      ) : (
        <div className="rounded border border-gray-200 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {f.name?.trim() || "Plancher bas"}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <TextField
              label="Nom (optionnel)"
              value={f.name ?? ""}
              onChange={(v) => set({ ...f, name: v || null })}
              placeholder="ex : Plancher salon"
            />
            <SelectField
              label="Type"
              value={f.material}
              onChange={(v) => set({ ...f, material: v })}
              options={[{ key: "", label: "(choisir)" }, ...catalogs.low_floors]}
            />
            <NumberField
              label="Surface (m²)"
              value={f.surface_m2}
              onChange={(v) => set({ ...f, surface_m2: v })}
            />
            <SelectField
              label="Donnant sur"
              value={f.adjacent ?? "local_non_chauffe"}
              onChange={(v) => set({ ...f, adjacent: v })}
              options={FLOOR_ADJACENT_OPTIONS}
            />
            <SelectField
              label="Année d'isolation"
              value={f.insulation_year ?? ""}
              onChange={(v) => set({ ...f, insulation_year: (v || null) as LowFloor["insulation_year"] })}
              options={INSULATION_YEAR_OPTIONS}
            />
            <SelectField
              label="Type d'isolation"
              value={f.isolation_type ?? ""}
              onChange={(v) => set({ ...f, isolation_type: (v || null) as LowFloor["isolation_type"] })}
              options={LOW_FLOOR_ISOLATION_TYPE_OPTIONS}
            />
            <NumberField
              label="Épaisseur isolant (cm)"
              value={f.insulation_thickness_cm ?? 0}
              onChange={(v) => set({ ...f, insulation_thickness_cm: v > 0 ? v : null })}
            />
            {(f.adjacent === "terre_plein" || f.adjacent === "vide_sanitaire" || f.adjacent === "sous_sol_non_chauffe") && (
              <>
                <NumberField
                  label="Périmètre plancher (m)"
                  value={f.perimeter_m ?? 0}
                  onChange={(v) => set({ ...f, perimeter_m: v > 0 ? v : null })}
                />
                <NumberField
                  label="Surface plancher immeuble (m²)"
                  value={f.building_surface_m2 ?? 0}
                  onChange={(v) => set({ ...f, building_surface_m2: v > 0 ? v : null })}
                />
                <NumberField
                  label="Périmètre plancher immeuble (m)"
                  value={f.building_perimeter_m ?? 0}
                  onChange={(v) => set({ ...f, building_perimeter_m: v > 0 ? v : null })}
                />
              </>
            )}
          </div>
          {LNC_ADJACENTS.has(f.adjacent ?? "") && (
            <BCoefficientFields spec={f} onChange={(partial) => set({ ...f, ...partial })} />
          )}
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
              Précision avancée (λ)
            </summary>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <NumberField
                label="Lambda λ (W/m·K)"
                value={f.insulation_lambda_w_m_k ?? 0.042}
                onChange={(v) => set({ ...f, insulation_lambda_w_m_k: v })}
              />
            </div>
          </details>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={() => set(undefined)}
              className="text-xs text-red-700 underline"
            >
              Retirer le plancher bas
            </button>
          </div>
        </div>
      )}
    </SectionShell>
  );
}

const ORIENTATION_OPTIONS = [
  { key: "S",  label: "Sud (S) — apports solaires maximum" },
  { key: "SE", label: "Sud-Est (SE)" },
  { key: "SO", label: "Sud-Ouest (SO)" },
  { key: "E",  label: "Est (E)" },
  { key: "O",  label: "Ouest (O)" },
  { key: "NE", label: "Nord-Est (NE)" },
  { key: "NO", label: "Nord-Ouest (NO)" },
  { key: "N",  label: "Nord (N) — apports solaires faibles" },
  { key: "horizontal", label: "Horizontale (fenêtre de toit, velux)" },
];

const VOLET_OPTIONS = [
  { key: "aucun", label: "Aucun volet" },
  { key: "volet_bois_metal_simple", label: "Volet bois ou métal simple" },
  { key: "volet_persienne", label: "Persienne" },
  { key: "volet_roulant_pvc", label: "Volet roulant PVC" },
  { key: "volet_roulant_alu_isole", label: "Volet roulant aluminium isolé" },
  { key: "jalousie_orientable", label: "Jalousie orientable / BSO" },
];

const MASQUE_OPTIONS = [
  { key: "aucun", label: "Aucun masque proche" },
  { key: "loggia", label: "Baie en fond de loggia" },
  { key: "balcon", label: "Baie sous balcon ou auvent" },
  { key: "paroi_laterale_sud", label: "Paroi latérale au Sud (mur perpendiculaire)" },
];

function WindowsSection({ payload, setPayload, catalogs, tutorial, errors }: SectionProps) {
  const add = () =>
    setPayload({
      ...payload,
      windows: [
        ...payload.windows,
        {
          glazing: "",
          surface_m2: 0,
          orientation: "S",
          volet_type: "aucun",
          masque_proche: "aucun",
        },
      ],
    });
  const update = (idx: number, partial: Partial<Window>) =>
    setPayload({
      ...payload,
      windows: payload.windows.map((w, i) => (i === idx ? { ...w, ...partial } : w)),
    });
  const remove = (idx: number) =>
    setPayload({ ...payload, windows: payload.windows.filter((_, i) => i !== idx) });

  // V5 — Toggle Velux : coche = fenêtre de toit (baie_inclinaison="moins_25"
  // + orientation="horizontal"). On conserve les valeurs précédentes pour les
  // restaurer au décochage.
  const toggleVelux = (idx: number, isVelux: boolean) => {
    setPayload((p) => {
      const w = p.windows[idx];
      if (!w) return p;
      const next: Window = isVelux
        ? {
            ...w,
            is_velux: true,
            // Auto-set inclinaison + orientation si pas déjà renseignées
            baie_inclinaison: w.baie_inclinaison ?? "moins_25",
            orientation: w.orientation ?? "horizontal",
          }
        : {
            ...w,
            is_velux: false,
            // Restore : si l'user n'avait rien touché, retombe sur verticale/S
            baie_inclinaison: w.baie_inclinaison === "moins_25" ? "verticale" : w.baie_inclinaison,
            orientation: w.orientation === "horizontal" ? "S" : w.orientation,
          };
      return {
        ...p,
        windows: p.windows.map((win, i) => (i === idx ? next : win)),
      };
    });
  };

  return (
    <SectionShell title="Fenêtres" tutorial={tutorial} errors={errors} onAdd={add} addLabel="+ Ajouter une fenêtre">
      <p className="mb-3 text-xs text-gray-600">
        L'orientation est <strong>essentielle</strong> : une baie Sud apporte
        environ 5× plus de chaleur gratuite qu'une baie Nord. Les volets
        extérieurs et masques proches (loggia, balcon) sont aussi pris en
        compte par le moteur 3CL officiel.
      </p>
      {payload.windows.map((w, i) => (
        <div key={i} className="mb-3 rounded border border-gray-200 p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            <span>{w.name?.trim() || `Fenêtre n°${i + 1}`}</span>
            {w.orientation && (
              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-800">
                {w.orientation}
              </span>
            )}
            {w.is_velux && (
              <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                Velux
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <TextField
              label="Nom (optionnel)"
              value={w.name ?? ""}
              onChange={(v) => update(i, { name: v || null })}
              placeholder="ex : Baie salon, Fenêtre cuisine"
            />
            <SelectField
              label="Vitrage"
              value={w.glazing}
              onChange={(v) => update(i, { glazing: v })}
              options={[{ key: "", label: "(choisir)" }, ...catalogs.windows]}
            />
            <NumberField
              label="Surface (m²)"
              value={w.surface_m2}
              onChange={(v) => update(i, { surface_m2: v })}
            />
            <SelectField
              label="Orientation (apports solaires)"
              value={w.orientation ?? "S"}
              onChange={(v) => update(i, { orientation: v as Orientation })}
              options={ORIENTATION_OPTIONS}
            />
            <SelectField
              label="Volet / protection solaire"
              value={w.volet_type ?? "aucun"}
              onChange={(v) => update(i, { volet_type: v as Window["volet_type"] })}
              options={VOLET_OPTIONS}
            />
            <SelectField
              label="Masque proche"
              value={w.masque_proche ?? "aucun"}
              onChange={(v) =>
                update(i, { masque_proche: v as Window["masque_proche"] })
              }
              options={MASQUE_OPTIONS}
            />
            {w.masque_proche && w.masque_proche !== "aucun" && (
              <NumberField
                label="Avancée du masque (m)"
                value={w.masque_avancee_m ?? 0}
                onChange={(v) => update(i, { masque_avancee_m: v })}
              />
            )}
          </div>
          {/* V5 — Toggle Velux / fenêtre de toit */}
          <label className="mt-3 inline-flex cursor-pointer items-center gap-2 rounded border border-amber-300 bg-amber-50/50 px-3 py-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
              checked={!!w.is_velux}
              onChange={(e) => toggleVelux(i, e.target.checked)}
              data-testid={`window-velux-toggle-${i}`}
            />
            <span className="font-medium text-amber-900">
              Velux / fenêtre de toit
            </span>
            <span className="text-xs text-amber-700">
              (auto-set : inclinaison {w.is_velux ? "< 25°" : "verticale"} + orientation {w.is_velux ? "horizontale" : "Sud"})
            </span>
          </label>
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
              Caractéristiques détaillées (menuiserie)
            </summary>
            <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <SelectField
                label="Inclinaison de la baie"
                value={w.baie_inclinaison ?? "verticale"}
                onChange={(v) => update(i, { baie_inclinaison: v as Window["baie_inclinaison"] })}
                options={BAIE_INCLINAISON_OPTIONS}
              />
              <SelectField
                label="Matériau menuiserie"
                value={w.menuiserie_type ?? "pvc"}
                onChange={(v) => update(i, { menuiserie_type: v as Window["menuiserie_type"] })}
                options={MENUISERIE_TYPE_OPTIONS}
              />
              <SelectField
                label="Type de fenêtre"
                value={w.type_fenetre ?? "battante"}
                onChange={(v) => update(i, { type_fenetre: v as Window["type_fenetre"] })}
                options={TYPE_FENETRE_OPTIONS}
              />
              <SelectField
                label="Position dans la baie"
                value={w.position_menuiserie ?? "tunnel"}
                onChange={(v) => update(i, { position_menuiserie: v as Window["position_menuiserie"] })}
                options={POSITION_MENUISERIE_OPTIONS}
              />
              <SelectField
                label="Largeur dormant (Lp)"
                value={w.largeur_dormant_cm == null ? "" : String(w.largeur_dormant_cm)}
                onChange={(v) =>
                  update(i, {
                    largeur_dormant_cm: v === "" ? null : (parseInt(v, 10) as 5 | 10),
                  })
                }
                options={LARGEUR_DORMANT_OPTIONS}
              />
              <NumberField
                label="Hauteur baie (m, optionnel pour balcon)"
                value={w.baie_hauteur_m ?? 0}
                onChange={(v) => update(i, { baie_hauteur_m: v > 0 ? v : null })}
              />
            </div>
          </details>
          <div className="mt-2 flex justify-end">
            <RemoveButton onClick={() => remove(i)} />
          </div>
        </div>
      ))}
    </SectionShell>
  );
}

function DoorsSection({ payload, setPayload, catalogs, tutorial, errors }: SectionProps) {
  const add = () =>
    setPayload({
      ...payload,
      doors: [...payload.doors, { door_type: "", surface_m2: 0 }],
    });
  const update = (idx: number, partial: Partial<Door>) =>
    setPayload({
      ...payload,
      doors: payload.doors.map((d, i) => (i === idx ? { ...d, ...partial } : d)),
    });
  const remove = (idx: number) =>
    setPayload({ ...payload, doors: payload.doors.filter((_, i) => i !== idx) });

  return (
    <SectionShell title="Portes (donnant sur l'extérieur)" tutorial={tutorial} errors={errors} onAdd={add} addLabel="+ Ajouter une porte">
      {payload.doors.map((d, i) => (
        <div key={i} className="rounded border border-gray-200 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {d.name?.trim() || `Porte n°${i + 1}`}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <TextField
              label="Nom (optionnel)"
              value={d.name ?? ""}
              onChange={(v) => update(i, { name: v || null })}
              placeholder="ex : Porte d'entrée"
            />
            <SelectField
              label="Type"
              value={d.door_type}
              onChange={(v) => update(i, { door_type: v })}
              options={[{ key: "", label: "(choisir)" }, ...catalogs.doors]}
            />
            <NumberField
              label="Surface (m²)"
              value={d.surface_m2}
              onChange={(v) => update(i, { surface_m2: v })}
            />
          </div>
          <div className="mt-2 flex justify-end">
            <RemoveButton onClick={() => remove(i)} />
          </div>
        </div>
      ))}
    </SectionShell>
  );
}

function SystemsSection({ payload, setPayload, catalogs, tutorial, errors }: SectionProps) {
  const heating = payload.heating[0] ?? { generator: "", share: 1 };
  // 3CL § 7 — 2e générateur de chauffage (appoint) optionnel. Présent ssi
  // payload.heating contient un second élément.
  const appoint = payload.heating[1] ?? null;
  const hasAppoint = appoint !== null;

  // Met à jour le générateur PRINCIPAL (heating[0]) en préservant l'appoint.
  const updateHeating = (partial: Partial<HeatingSystem>) => {
    // Si l'utilisateur décoche le collectif, on réinitialise implicitement le
    // comptage individuel (sinon l'état resterait incohérent côté backend).
    const safe: Partial<HeatingSystem> =
      partial.is_collective === false
        ? { ...partial, has_individual_metering: false }
        : partial;
    const next = [{ ...heating, ...safe }, ...payload.heating.slice(1)];
    setPayload({ ...payload, heating: next });
  };

  // Met à jour le générateur d'APPOINT (heating[1]).
  const updateAppoint = (partial: Partial<HeatingSystem>) => {
    if (!appoint) return;
    setPayload({
      ...payload,
      heating: [heating, { ...appoint, ...partial }],
    });
  };

  // Ajoute un générateur d'appoint : on répartit la part (base 0.7 / appoint
  // 0.3 par défaut, modifiable) et on bascule la config sur « base + appoint ».
  const addAppoint = () => {
    setPayload({
      ...payload,
      heating: [
        { ...heating, share: 0.7, config: "avec_appoint_bois" },
        {
          generator: "",
          share: 0.3,
          config: "avec_appoint_bois",
          regulation: "radiateur_eau_sans_thermostatique",
          distribution: "pas_de_reseau",
          distribution_isolated: false,
        },
      ],
    });
  };

  // Retire l'appoint : retour à un système unique (share 1.0, config simple).
  const removeAppoint = () => {
    setPayload({
      ...payload,
      heating: [{ ...heating, share: 1.0, config: "simple" }],
    });
  };
  const updateEcs = (partial: Partial<EcsSystem>) =>
    setPayload({ ...payload, ecs: { ...payload.ecs, ...partial } });
  const updateVent = (partial: Partial<Ventilation>) =>
    setPayload({ ...payload, ventilation: { ...payload.ventilation, ...partial } });
  const updateCooling = (partial: Partial<Cooling>) =>
    setPayload({
      ...payload,
      cooling: { ...(payload.cooling ?? {}), ...partial },
    });
  const updateRenewable = (partial: Partial<Renewable>) =>
    setPayload({
      ...payload,
      renewable: { ...(payload.renewable ?? {}), ...partial },
    });

  return (
    <SectionShell title="Systèmes (chauffage / eau chaude sanitaire / ventilation)" tutorial={tutorial} errors={errors}>
      <div className="space-y-5">
        {/* Chauffage */}
        <div className="rounded border border-gray-200 p-3">
          <div className="mb-2 flex items-center justify-between gap-3">
            <h3 className="text-sm font-semibold text-gray-800">Chauffage principal</h3>
            {hasAppoint && (
              <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-800">
                {Math.round((heating.share ?? 1) * 100)} % du besoin
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SelectField
              label="Générateur"
              value={heating.generator}
              onChange={(v) => updateHeating({ generator: v })}
              options={[{ key: "", label: "(choisir)" }, ...catalogs.heating_generators]}
            />
            <NumberField
              label="Puissance nominale (kW, optionnel pour chaudière/combustion)"
              value={heating.nominal_power_kw ?? 0}
              onChange={(v) => updateHeating({ nominal_power_kw: v > 0 ? v : null })}
            />
            {hasAppoint && (
              <NumberField
                label="Part couverte par le générateur principal (%)"
                value={Math.round((heating.share ?? 1) * 100)}
                onChange={(v) =>
                  updateHeating({ share: Math.min(1, Math.max(0, v / 100)) })
                }
              />
            )}
          </div>
          {/* 3CL § 8 — chaîne émission / distribution / régulation. Par défaut,
              le moteur applique un forfait conservateur ; la saisie le surcharge. */}
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
              Émission / distribution / régulation
            </summary>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SelectField
                label="Émetteur"
                value={heating.emitter ?? ""}
                onChange={(v) => updateHeating({ emitter: v || undefined })}
                options={[{ key: "", label: "(par défaut moteur)" }, ...EMITTER_OPTIONS]}
              />
              <SelectField
                label="Distribution"
                value={heating.distribution ?? ""}
                onChange={(v) => updateHeating({ distribution: v || undefined })}
                options={[{ key: "", label: "(par défaut moteur)" }, ...DISTRIBUTION_OPTIONS]}
              />
              <SelectField
                label="Régulation"
                value={heating.regulation ?? ""}
                onChange={(v) => updateHeating({ regulation: v || undefined })}
                options={[
                  { key: "", label: "(par défaut moteur : sans thermostatique)" },
                  ...REGULATION_OPTIONS,
                ]}
              />
              <label className="flex items-center gap-2 self-end text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={heating.distribution_isolated ?? false}
                  onChange={(e) =>
                    updateHeating({ distribution_isolated: e.target.checked })
                  }
                />
                Réseau de distribution calorifugé (isolé)
              </label>
              <NumberField
                label="Année d'installation du générateur"
                value={heating.year_install ?? 0}
                onChange={(v) =>
                  updateHeating({ year_install: v > 1900 ? Math.round(v) : null })
                }
              />
            </div>
          </details>
          <details className="mt-2">
            <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
              Options avancées (installation collective, régulation d'intermittence)
            </summary>
            <div className="mt-3 space-y-2">
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={heating.is_collective ?? false}
                  onChange={(e) => updateHeating({ is_collective: e.target.checked })}
                />
                Chauffage collectif d'immeuble
              </label>
              {heating.is_collective && (
                <label className="ml-5 flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={heating.has_individual_metering ?? false}
                    onChange={(e) =>
                      updateHeating({ has_individual_metering: e.target.checked })
                    }
                  />
                  Comptage individuel sur l'installation
                </label>
              )}
              <div className="pt-1">
                <SelectField
                  label="Équipement d'intermittence (régulation)"
                  value={heating.intermittence_equipement ?? ""}
                  onChange={(v) =>
                    updateHeating({
                      intermittence_equipement:
                        (v || null) as HeatingSystem["intermittence_equipement"],
                    })
                  }
                  options={INTERMITTENCE_OPTIONS}
                />
              </div>
            </div>
          </details>
          {/* 3CL § 7 — 2e générateur (appoint) : poêle bois, appoint électrique… */}
          <div className="mt-3 border-t border-dashed border-gray-200 pt-3">
            {!hasAppoint ? (
              <button
                type="button"
                onClick={addAppoint}
                className="text-sm text-blue-900 underline"
              >
                + Ajouter un générateur d'appoint
              </button>
            ) : (
              <div className="rounded border border-gray-200 bg-gray-50 p-3">
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-gray-800">
                    Générateur d'appoint
                  </h4>
                  <span className="rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-bold text-blue-800">
                    {Math.round((appoint.share ?? 0) * 100)} % du besoin
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <SelectField
                    label="Générateur d'appoint"
                    value={appoint.generator}
                    onChange={(v) => updateAppoint({ generator: v })}
                    options={[{ key: "", label: "(choisir)" }, ...catalogs.heating_generators]}
                  />
                  <NumberField
                    label="Part couverte par l'appoint (%)"
                    value={Math.round((appoint.share ?? 0) * 100)}
                    onChange={(v) =>
                      updateAppoint({ share: Math.min(1, Math.max(0, v / 100)) })
                    }
                  />
                  <SelectField
                    label="Configuration"
                    value={appoint.config ?? "avec_appoint_bois"}
                    onChange={(v) => {
                      const cfg = v as HeatingConfig;
                      // La config s'applique à l'installation : on la propage aux
                      // deux générateurs pour rester cohérent côté backend.
                      setPayload({
                        ...payload,
                        heating: [
                          { ...heating, config: cfg },
                          { ...appoint, config: cfg },
                        ],
                      });
                    }}
                    options={HEATING_CONFIG_OPTIONS.filter((o) => o.key !== "simple")}
                  />
                  <NumberField
                    label="Année d'installation de l'appoint"
                    value={appoint.year_install ?? 0}
                    onChange={(v) =>
                      updateAppoint({ year_install: v > 1900 ? Math.round(v) : null })
                    }
                  />
                </div>
                <p className="mt-2 text-[11px] text-gray-500">
                  Les parts principal + appoint doivent totaliser 100 %. Actuel :{" "}
                  {Math.round(((heating.share ?? 0) + (appoint.share ?? 0)) * 100)} %.
                </p>
                <div className="mt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={removeAppoint}
                    className="text-xs text-red-700 underline"
                  >
                    Retirer l'appoint
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
        {/* ECS */}
        <div className="rounded border border-gray-200 p-3">
          <h3 className="mb-2 text-sm font-semibold text-gray-800">Eau chaude sanitaire</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SelectField
              label="Générateur"
              value={payload.ecs.generator}
              onChange={(v) =>
                updateEcs(
                  isInstantEcs(v)
                    ? { generator: v, storage_volume_L: null }
                    : { generator: v },
                )
              }
              options={[{ key: "", label: "(choisir)" }, ...catalogs.ecs_generators]}
            />
            {!isInstantEcs(payload.ecs.generator) && (
            <NumberField
              label="Volume du ballon (L, si applicable)"
              value={payload.ecs.storage_volume_L ?? 0}
              onChange={(v) => updateEcs({ storage_volume_L: v > 0 ? v : null })}
            />
            )}
            <SelectField
              label="Emplacement du ballon"
              value={payload.ecs.location ?? "volume_habitable"}
              onChange={(v) => updateEcs({ location: v as EcsSystem["location"] })}
              options={[
                { key: "volume_habitable", label: "Dans le volume habitable" },
                { key: "hors_volume_habitable", label: "Hors volume habitable (garage, etc.)" },
              ]}
            />
            <NumberField
              label="Année d'installation du générateur d'eau chaude sanitaire"
              value={payload.ecs.year_install ?? 0}
              onChange={(v) =>
                updateEcs({ year_install: v > 1900 ? Math.round(v) : null })
              }
            />
          </div>
          {/* 3CL § 13.2 — ECS collective d'immeuble (réseau de bouclage).
              Symétrie avec le chauffage collectif déjà exposé plus haut. */}
          <div className="mt-3 space-y-2 rounded border border-gray-100 bg-gray-50 p-3">
            <label className="flex items-center gap-2 text-xs text-gray-700">
              <input
                type="checkbox"
                checked={payload.ecs.is_collective_ecs ?? false}
                onChange={(e) =>
                  updateEcs({ is_collective_ecs: e.target.checked })
                }
              />
              Eau chaude sanitaire collective d'immeuble (production centralisée)
            </label>
            {payload.ecs.is_collective_ecs && (
              <label className="ml-5 flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={payload.ecs.collective_network_insulated ?? false}
                  onChange={(e) =>
                    updateEcs({ collective_network_insulated: e.target.checked })
                  }
                />
                Réseau de bouclage calorifugé (isolé)
              </label>
            )}
          </div>
          {(isCetGenerator(payload.ecs.generator) ||
            (payload.ecs.location ?? "volume_habitable") === "volume_habitable" ||
            isBallonOrCetGenerator(payload.ecs.generator)) && (
            <details className="mt-2" open>
              <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
                Précisions eau chaude sanitaire (chauffe-eau thermodynamique, configuration des pièces, bonus stockage)
              </summary>
              <div className="mt-3 space-y-2">
                {isCetGenerator(payload.ecs.generator) && (
                  <SelectField
                    label="Alimentation du chauffe-eau thermodynamique"
                    value={payload.ecs.alimentation_cet ?? ""}
                    onChange={(v) =>
                      updateEcs({
                        alimentation_cet:
                          (v || null) as EcsSystem["alimentation_cet"],
                      })
                    }
                    options={ALIMENTATION_CET_OPTIONS}
                  />
                )}
                {(payload.ecs.location ?? "volume_habitable") ===
                  "volume_habitable" && (
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={payload.ecs.pieces_contigues ?? true}
                      onChange={(e) =>
                        updateEcs({ pieces_contigues: e.target.checked })
                      }
                    />
                    Pièces contiguës (impact matrice Rd eau chaude sanitaire 3×3)
                  </label>
                )}
                {isBallonOrCetGenerator(payload.ecs.generator) && (
                  <label className="flex items-center gap-2 text-xs text-gray-700">
                    <input
                      type="checkbox"
                      checked={payload.ecs.is_recommended_ballon ?? false}
                      onChange={(e) =>
                        updateEcs({ is_recommended_ballon: e.target.checked })
                      }
                    />
                    Ballon recommandé (bonus Rs × 1,08)
                  </label>
                )}
              </div>
            </details>
          )}
        </div>
        {/* Ventilation */}
        <div className="rounded border border-gray-200 p-3">
          <h3 className="mb-2 text-sm font-semibold text-gray-800">Ventilation</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <SelectField
              label="Type"
              value={payload.ventilation.type}
              onChange={(v) => updateVent({ type: v })}
              options={[{ key: "", label: "(choisir)" }, ...catalogs.ventilation_types]}
            />
            <div className="space-y-1 self-end">
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={payload.ventilation.has_joints ?? true}
                  onChange={(e) => updateVent({ has_joints: e.target.checked })}
                />
                Joints d'étanchéité aux portes et fenêtres
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={payload.ventilation.has_trappe_cheminee ?? true}
                  onChange={(e) => updateVent({ has_trappe_cheminee: e.target.checked })}
                />
                Trappe sur conduit de cheminée (si cheminée présente)
              </label>
            </div>
          </div>
        </div>
        {/* Climatisation */}
        <div className="rounded border border-gray-200 p-3">
          <h3 className="mb-2 text-sm font-semibold text-gray-800">Climatisation</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <NumberField
              label="Surface climatisée (% de la surface habitable)"
              value={Math.round((payload.cooling?.percent_climatized ?? 0) * 100)}
              onChange={(v) =>
                updateCooling({ percent_climatized: Math.min(1, Math.max(0, v / 100)) })
              }
            />
            <SelectField
              label="Énergie de la clim"
              value={payload.cooling?.energy ?? "electricite"}
              onChange={(v) => updateCooling({ energy: v as Cooling["energy"] })}
              options={[
                { key: "electricite", label: "Électricité" },
                { key: "gaz_naturel", label: "Gaz naturel" },
              ]}
            />
            {payload.meta.type === "appartement" && (
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={payload.cooling?.is_top_floor ?? false}
                  onChange={(e) => updateCooling({ is_top_floor: e.target.checked })}
                />
                Appartement au dernier étage (surchauffe)
              </label>
            )}
          </div>
          {/* V0.9.14 — Confort d'été (arbre décisionnel arrêté 13/04/2022, Guide DPE 2024 p. 104-105) */}
          <details className="mt-3" open>
            <summary className="cursor-pointer text-xs text-gray-500 hover:text-gray-700">
              Confort d'été (saisie pour arbre décisionnel arrêté 13/04/2022)
            </summary>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SelectField
                label="Protection solaire extérieure (baies S/E/O)"
                value={payload.cooling?.protection_solaire ?? ""}
                onChange={(v) =>
                  updateCooling({
                    protection_solaire:
                      (v || null) as Cooling["protection_solaire"],
                  })
                }
                options={[
                  { key: "", label: "(auto — déduit des fenêtres)" },
                  { key: "aucune", label: "Aucune" },
                  { key: "partielle", label: "Partielle (≥ 50 % des baies S/E/O)" },
                  { key: "totale", label: "Totale (≥ 80 % des baies S/E/O)" },
                ]}
              />
              <div className="space-y-1 self-end">
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={payload.cooling?.ventilation_traversante ?? false}
                    onChange={(e) =>
                      updateCooling({ ventilation_traversante: e.target.checked })
                    }
                  />
                  Logement déclaré traversant (override règle &lt; 75 %/orientation)
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={payload.cooling?.brasseur_air ?? false}
                    onChange={(e) =>
                      updateCooling({ brasseur_air: e.target.checked })
                    }
                  />
                  Brasseur d'air fixe — ventilateur de plafond (ventilateurs sur pied exclus)
                </label>
              </div>
            </div>
          </details>
        </div>
        {/* Énergies renouvelables */}
        <div className="rounded border border-gray-200 p-3">
          <h3 className="mb-2 text-sm font-semibold text-gray-800">Énergies renouvelables</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <NumberField
              label="Surface panneaux photovoltaïques (m²)"
              value={payload.renewable?.pv_capteurs_m2 ?? 0}
              onChange={(v) => updateRenewable({ pv_capteurs_m2: v })}
            />
            <div className="space-y-1 self-end">
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={payload.renewable?.has_chauffage_solaire ?? false}
                  onChange={(e) => updateRenewable({ has_chauffage_solaire: e.target.checked })}
                />
                Chauffage solaire (planchers / radiateurs)
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={payload.renewable?.has_ecs_solaire ?? false}
                  onChange={(e) => updateRenewable({ has_ecs_solaire: e.target.checked })}
                />
                Eau chaude sanitaire solaire (chauffe-eau solaire individuel)
              </label>
              {/* 3CL § 20.1 — installation solaire ancienne (> 5 ans) : couverture
                  Fecs inférieure. Défaut moteur conservateur = ancienne. */}
              {(payload.renewable?.has_ecs_solaire ||
                payload.renewable?.has_chauffage_solaire) && (
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={payload.renewable?.installation_solaire_ancienne ?? true}
                    onChange={(e) =>
                      updateRenewable({
                        installation_solaire_ancienne: e.target.checked,
                      })
                    }
                  />
                  Installation solaire ancienne (&gt; 5 ans)
                </label>
              )}
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

// =============================================================================
// Primitives UI
// =============================================================================

function SectionShell({
  title,
  children,
  onAdd,
  addLabel,
  help,
  tutorial,
  errors,
}: {
  title: string;
  children: React.ReactNode;
  onAdd?: () => void;
  addLabel?: string;
  help?: string;
  tutorial?: TutorialOption;
  errors?: string[];
}) {
  const hasErrors = !!errors && errors.length > 0;
  return (
    <section
      className={`rounded border bg-white p-6 ${
        hasErrors ? "border-red-400 ring-1 ring-red-300" : "border-gray-200"
      }`}
    >
      <header className="mb-4 flex items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold">{title}</h2>
          {hasErrors && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">
              {errors.length} champ{errors.length > 1 ? "s" : ""} à compléter
            </span>
          )}
          <TutorialButton tutorial={tutorial} />
        </div>
        {onAdd && (
          <button
            type="button"
            onClick={onAdd}
            className="rounded border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100"
          >
            {addLabel}
          </button>
        )}
      </header>
      {hasErrors && (
        <div
          role="alert"
          className="mb-3 rounded border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-800"
        >
          <p className="font-semibold">Champs obligatoires à renseigner :</p>
          <ul className="ml-4 mt-1 list-disc">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      {help && <p className="mb-3 text-xs text-gray-500">{help}</p>}
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function TextField({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  const id = `f-${label}-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-700">
        {label}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
      />
    </div>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  const id = `f-${label}-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-700">
        {label}
      </label>
      <input
        id={id}
        type="number"
        value={Number.isFinite(value) ? value : 0}
        step="any"
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700"
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: CatalogOption[];
}) {
  const id = `f-${label}-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-700">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      >
        {options.map((o) => (
          <option key={o.key} value={o.key}>
            {o.label ?? o.key}
          </option>
        ))}
      </select>
    </div>
  );
}

// V5 — Sélecteur avec catégories (<optgroup>). Utilisé pour les murs où le
// catalogue contient 71 entrées rangées par famille (pierre, pisé, blocs
// béton, brique, monomur, béton cellulaire…). Le regroupement évite un
// dropdown monolithique illisible et aide l'utilisateur à retrouver "mon
// mur, c'est un parpaing creux de 20cm".
function GroupedSelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ label: string; items: Array<{ key: string; label?: string }> }>;
}) {
  const id = `f-${label}-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <div>
      <label htmlFor={id} className="mb-1 block text-xs font-medium text-gray-700">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm text-gray-900 focus:border-blue-700 focus:outline-none focus:ring-1 focus:ring-blue-700 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      >
        <option value="">— Choisir —</option>
        {options.map((group) => (
          <optgroup key={group.label} label={group.label}>
            {group.items.map((it) => (
              <option key={it.key} value={it.key}>
                {it.label ?? it.key}
              </option>
            ))}
          </optgroup>
        ))}
      </select>
    </div>
  );
}

// V5 — Regroupement des murs par famille pour l'UI. Le moteur 3CL utilise
// la clé technique (catalog.walls) ; ce mapping est purement présentationnel.
// Catégories alignées sur les 71 entrées de materials_catalog.yaml.
// (extrait dans wallCategories.ts pour pouvoir le tester unitairement)
import { groupWallOptions } from "./wallCategories";

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <div className="flex items-end">
      <button
        type="button"
        onClick={onClick}
        className="text-xs text-red-700 underline"
      >
        Retirer
      </button>
    </div>
  );
}

// =============================================================================
// Sous-onglet "Passer à l'action" : liens externes pour concrétiser les
// travaux (éligibilité MaPrimeRénov', annuaire artisans RGE, autres
// ressources utiles). Centralise tout ce qui n'est pas de la simulation.
// =============================================================================

function PasserALActionPanel() {
  return (
    <section className="rounded border border-gray-200 bg-white p-6">
      <h2 className="text-xl font-bold">Passer à l&apos;action</h2>
      <p className="mt-1 text-sm text-gray-600">
        Une fois votre simulation validée, voici les ressources officielles
        pour concrétiser vos travaux.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <a
          href="https://france-renov.gouv.fr/aides"
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded border border-blue-200 bg-blue-50 p-4 hover:border-blue-400"
        >
          <h3 className="font-semibold text-blue-900">
            Vérifier mon éligibilité MaPrimeRénov&apos;
          </h3>
          <p className="mt-1 text-sm text-blue-900">
            Simuler le montant de l&apos;aide (bleu / jaune / violet / rose)
            selon vos revenus et votre projet, sur le site officiel
            France&nbsp;Rénov&apos;.
          </p>
          <p className="mt-2 text-xs font-medium text-blue-900 underline">
            france-renov.gouv.fr/aides ↗
          </p>
        </a>

        <a
          href="https://france-renov.gouv.fr/annuaire-rge"
          target="_blank"
          rel="noopener noreferrer"
          className="block rounded border border-emerald-200 bg-emerald-50 p-4 hover:border-emerald-400"
        >
          <h3 className="font-semibold text-emerald-900">
            Trouver un artisan RGE
          </h3>
          <p className="mt-1 text-sm text-emerald-900">
            Annuaire officiel des artisans Reconnus Garants de
            l&apos;Environnement près de chez vous. Obligatoire pour bénéficier
            de la plupart des aides.
          </p>
          <p className="mt-2 text-xs font-medium text-emerald-900 underline">
            france-renov.gouv.fr/annuaire-rge ↗
          </p>
        </a>
      </div>

      <div className="mt-6 rounded bg-gray-50 p-3 text-xs text-gray-700">
        <strong>Bon à savoir&nbsp;:</strong> les montants d&apos;aides et la
        liste des artisans RGE sont mis à jour par l&apos;État — ces liens
        renvoient vers la source officielle et restent à jour.
      </div>
    </section>
  );
}
